import React, { useMemo } from "react";
import { Drawer, Divider } from "@mui/material";
import { AiOutlineClose } from "react-icons/ai";
import { FaExclamationTriangle } from "react-icons/fa";

function ProductDrawer({ openDrawer, setOpenDrawer, selectedProduct }) {
  const imageSrc = useMemo(() => {
    if (!selectedProduct?.image?.data || !selectedProduct?.image?.contentType) return null;
    return `data:${selectedProduct.image.contentType};base64,${selectedProduct.image.data}`;
  }, [selectedProduct]);

  return (
    <Drawer
      anchor="right"
      open={openDrawer}
      onClose={() => setOpenDrawer(false)}
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: 420 },
          borderTopLeftRadius: 16,
          borderBottomLeftRadius: 16,
          overflow: "hidden",
          boxShadow: "0 24px 80px rgba(0,0,0,0.22)",
        },
      }}
    >
      {/* Header */}
      <div className="bg-white">
        <div className="flex items-start justify-between px-5 py-4">
          <div className="min-w-0">
            <h3 className="text-lg sm:text-xl font-extrabold text-slate-900 leading-tight">
              Product Details
            </h3>
            <p className="text-sm text-slate-500 mt-0.5">
              Review product information and specifications
            </p>
          </div>

          <button
            onClick={() => setOpenDrawer(false)}
            className="h-10 w-10 grid place-items-center rounded-xl border border-slate-200 bg-white text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition"
            aria-label="Close"
          >
            <AiOutlineClose size={20} />
          </button>
        </div>
        <Divider />
      </div>

      {/* Content */}
      <div className="h-full overflow-auto bg-slate-50">
        <div className="p-5">
          {!selectedProduct ? (
            <div className="rounded-2xl border bg-white p-5 text-center">
              <div className="mx-auto mb-3 inline-flex items-center gap-2 rounded-full bg-amber-50 px-3 py-1 text-amber-700 border border-amber-100 text-sm font-bold">
                <FaExclamationTriangle />
                No product selected
              </div>
              <p className="text-slate-600 text-sm">
                Please select a product to view details.
              </p>
            </div>
          ) : (
            <>
              {/* Image + Title Card */}
              <div className="rounded-2xl border bg-white overflow-hidden shadow-sm">
                <div className="relative h-52 w-full bg-slate-100">
                  {imageSrc ? (
                    <img
                      src={imageSrc}
                      alt={selectedProduct.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-slate-500">
                      No Image
                    </div>
                  )}

                  {/* Soft gradient overlay for premium look */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/35 via-black/10 to-transparent" />
                  <div className="absolute bottom-3 left-3 right-3">
                    <h4 className="text-white text-lg font-extrabold truncate">
                      {selectedProduct.name || "—"}
                    </h4>
                    <p className="text-white/85 text-xs mt-0.5 truncate">
                      {selectedProduct?.category?.name || "—"} •{" "}
                      {selectedProduct?.subcategory?.name || "—"}
                    </p>
                  </div>
                </div>

                <div className="p-4">
                  {/* Price */}
                  <div className="flex items-center justify-between gap-3">
                    <div>
                      <p className="text-xs font-bold text-slate-500">Price</p>
                      <p className="text-lg font-extrabold text-slate-900">
                        ₹{selectedProduct.price ?? "—"}
                      </p>
                    </div>

                    <span className="text-xs font-bold px-3 py-1 rounded-full bg-slate-100 text-slate-700 border">
                      Active
                    </span>
                  </div>

                  {/* Description */}
                  <div className="mt-4">
                    <p className="text-xs font-bold text-slate-500">Description</p>
                    <p className="text-sm text-slate-700 mt-1 leading-relaxed">
                      {selectedProduct.description || "No description available."}
                    </p>
                  </div>
                </div>
              </div>

              {/* Details Section */}
              <div className="mt-4 grid grid-cols-1 gap-4">
                <div className="rounded-2xl border bg-white p-4 shadow-sm">
                  <p className="text-sm font-extrabold text-slate-900">Category</p>
                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="rounded-xl bg-slate-50 border p-3">
                      <p className="text-xs font-bold text-slate-500">Category</p>
                      <p className="text-sm font-bold text-slate-900 mt-0.5">
                        {selectedProduct?.category?.name || "—"}
                      </p>
                    </div>
                    <div className="rounded-xl bg-slate-50 border p-3">
                      <p className="text-xs font-bold text-slate-500">Subcategory</p>
                      <p className="text-sm font-bold text-slate-900 mt-0.5">
                        {selectedProduct?.subcategory?.name || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Specifications */}
                <div className="rounded-2xl border bg-white p-4 shadow-sm">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-extrabold text-slate-900">Specifications</p>
                    <span className="text-xs font-bold px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 border">
                      {(selectedProduct.specifications?.length || 0) + " items"}
                    </span>
                  </div>

                  {selectedProduct.specifications &&
                  selectedProduct.specifications.length > 0 ? (
                    <div className="mt-3 grid grid-cols-1 gap-2">
                      {selectedProduct.specifications.map((spec, index) => (
                        <div
                          key={index}
                          className="flex items-start justify-between gap-4 rounded-xl border bg-slate-50 p-3"
                        >
                          <p className="text-sm font-bold text-slate-700">
                            {spec.key || "—"}
                          </p>
                          <p className="text-sm text-slate-900 font-semibold text-right">
                            {spec.value || "—"}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-600 mt-2">
                      No specifications available.
                    </p>
                  )}
                </div>
              </div>

              {/* Footer actions (UI only) */}
              <div className="mt-4 rounded-2xl border bg-white p-4 shadow-sm">
                <button
                  onClick={() => setOpenDrawer(false)}
                  className="w-full h-11 rounded-xl bg-slate-900 text-white font-extrabold hover:bg-slate-800 transition"
                >
                  Close
                </button>
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Tip: Keep product details accurate for faster approvals.
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </Drawer>
  );
}

export default ProductDrawer;